// DataAuditTab.jsx
// Drop-in replacement for the Data Audit tab in App.js
// Fetches live data from SharePoint via Graph API with raw table fallback

import { useState, useEffect, useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "./authConfig";
import { fetchDataAudit } from "./graphService";

const C = {
  dark: "#1a1a2e",
  gold: "#c9a84c",
  white: "#fff",
  border: "#e5e7eb",
  muted: "#6b7280",
  text: "#374151",
  green: "#27ae60",
  orange: "#e67e22",
  red: "#c0392b",
  blue: "#2980b9",
};

function TrustBadge({ v }) {
  const map = {
    high: [C.green, "#d4edda"],
    medium: ["#856404", "#fff3cd"],
    low: [C.red, "#f8d7da"],
    "n/a": ["#6b7280", "#f3f4f6"],
  };
  const [color, bg] = map[v] || map["n/a"];
  return (
    <span style={{ background: bg, color, fontSize: 11, padding: "2px 8px", borderRadius: 4, fontWeight: 600 }}>{v}</span>
  );
}

function ActionBadge({ v }) {
  const map = {
    keep: ["#155724", "#d4edda"],
    fix: ["#856404", "#fff3cd"],
    migrate: ["#004085", "#cce5ff"],
    replace: ["#721c24", "#f8d7da"],
    evaluate: ["#495057", "#e9ecef"],
  };
  const [color, bg] = map[v] || map.evaluate;
  return (
    <span style={{ background: bg, color, fontSize: 11, padding: "2px 8px", borderRadius: 4, fontWeight: 600, textTransform: "capitalize" }}>{v}</span>
  );
}

function Spinner() {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "40px 20px", color: C.muted, justifyContent: "center" }}>
      <div style={{ width: 18, height: 18, border: "2px solid #e5e7eb", borderTop: "2px solid " + C.gold, borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
      <span style={{ fontSize: 13 }}>Loading data sources from SharePoint...</span>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function RawTable({ values }) {
  if (!values || values.length === 0) return <div style={{ padding: 20, color: C.muted }}>No raw data available.</div>;
  const headers = values[0];
  const rows = values.slice(1).filter(function(r) { return r.some(function(c) { return c; }); });
  return (
    <div style={{ overflowX: "auto" }}>
      <div style={{ fontSize: 11, color: C.muted, marginBottom: 8 }}>
        Showing raw spreadsheet data. {rows.length} rows.
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
        <thead>
          <tr style={{ background: "#f9fafb", borderBottom: "2px solid " + C.border }}>
            {headers.map(function(h, i) {
              return <th key={i} style={{ padding: "6px 10px", textAlign: "left", fontWeight: 700, color: C.muted, whiteSpace: "nowrap", fontSize: 10, textTransform: "uppercase" }}>{h || "Col " + i}</th>;
            })}
          </tr>
        </thead>
        <tbody>
          {rows.slice(0, 100).map(function(row, i) {
            return (
              <tr key={i} style={{ borderBottom: "1px solid #f3f4f6", background: i % 2 === 0 ? C.white : "#fafafa" }}>
                {headers.map(function(_, j) {
                  return <td key={j} style={{ padding: "6px 10px", color: C.text, whiteSpace: "nowrap" }}>{String(row[j] || "")}</td>;
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
      {rows.length > 100 && <div style={{ padding: "8px 10px", fontSize: 11, color: C.muted }}>Showing first 100 of {rows.length} rows.</div>}
    </div>
  );
}

export default function DataAuditTab() {
  const { instance, accounts } = useMsal();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sources, setSources] = useState([]);
  const [rawValues, setRawValues] = useState(null);
  const [parseError, setParseError] = useState(null);
  const [showRaw, setShowRaw] = useState(false);
  const [auditFilter, setAuditFilter] = useState("all");
  const [lastUpdated, setLastUpdated] = useState(null);

  const getToken = useCallback(async function() {
    const res = await instance.acquireTokenSilent({ ...loginRequest, account: accounts[0] });
    return res.accessToken;
  }, [instance, accounts]);

  const loadData = useCallback(async function() {
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const result = await fetchDataAudit(token);
      setSources(result.rows);
      setRawValues(result.rawValues);
      setParseError(result.parseError);
      setLastUpdated(result.lastUpdated);
      if (result.parseError && result.rows.length === 0) {
        setShowRaw(true);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(function() { loadData(); }, []);

  if (loading) return <Spinner />;

  if (error) return (
    <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "16px 20px" }}>
      <div style={{ fontWeight: 700, color: C.red, marginBottom: 6 }}>Failed to load data sources</div>
      <div style={{ fontSize: 12, color: "#7f1d1d", marginBottom: 12 }}>{error}</div>
      <button onClick={loadData} style={{ padding: "6px 14px", background: C.dark, color: C.white, border: "none", borderRadius: 4, fontSize: 12, cursor: "pointer" }}>Retry</button>
    </div>
  );

  const filtered = auditFilter === "all" ? sources : sources.filter(function(s) { return s.action === auditFilter; });

  return (
    <div style={{ fontFamily: "system-ui, -apple-system, sans-serif", fontSize: 13 }}>

      {/* Header controls */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, marginBottom: 12 }}>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {["all", "keep", "fix", "migrate", "replace", "evaluate"].map(function(f) {
            const count = f === "all" ? sources.length : sources.filter(function(s) { return s.action === f; }).length;
            return (
              <button key={f} onClick={function() { setAuditFilter(f); setShowRaw(false); }}
                style={{ padding: "5px 12px", fontSize: 12, borderRadius: 4, border: "1px solid " + C.border, background: auditFilter === f && !showRaw ? C.dark : C.white, color: auditFilter === f && !showRaw ? C.white : C.text, cursor: "pointer", fontWeight: auditFilter === f && !showRaw ? 700 : 400, textTransform: "capitalize" }}>
                {f} ({count})
              </button>
            );
          })}
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {lastUpdated && <span style={{ fontSize: 11, color: C.muted }}>Updated {new Date(lastUpdated).toLocaleTimeString()}</span>}
          <button onClick={loadData} title="Refresh" style={{ padding: "5px 10px", border: "1px solid " + C.border, borderRadius: 4, background: C.white, cursor: "pointer", fontSize: 12, color: C.muted }}>↻ Refresh</button>
          {rawValues && (
            <button onClick={function() { setShowRaw(!showRaw); }}
              style={{ padding: "5px 12px", border: "1px solid " + C.border, borderRadius: 4, background: showRaw ? C.dark : C.white, color: showRaw ? C.white : C.muted, cursor: "pointer", fontSize: 12 }}>
              {showRaw ? "Parsed View" : "Raw Table"}
            </button>
          )}
        </div>
      </div>

      {parseError && !showRaw && (
        <div style={{ background: "#fef3c7", border: "1px solid #fde68a", borderRadius: 6, padding: "10px 14px", marginBottom: 12, fontSize: 12, color: "#78350f" }}>
          Partial parse: {parseError}. Showing {sources.length} rows.
          <button onClick={function() { setShowRaw(true); }} style={{ marginLeft: 10, fontSize: 11, color: "#92400e", textDecoration: "underline", background: "none", border: "none", cursor: "pointer" }}>Switch to raw view</button>
        </div>
      )}

      {showRaw ? (
        <div style={{ background: C.white, border: "1px solid " + C.border, borderRadius: 8, overflowX: "auto" }}>
          <RawTable values={rawValues} />
        </div>
      ) : (
        <div style={{ background: C.white, border: "1px solid " + C.border, borderRadius: 8, overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ background: "#f9fafb", borderBottom: "1px solid " + C.border }}>
                {["System / Report", "Current Owner", "Future Owner", "Trust", "Stories", "Action", "Priority"].map(function(h) {
                  return <th key={h} style={{ padding: "8px 12px", textAlign: "left", fontWeight: 700, whiteSpace: "nowrap", fontSize: 11, color: C.muted, textTransform: "uppercase", letterSpacing: 0.5 }}>{h}</th>;
                })}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={7} style={{ padding: "32px", textAlign: "center", color: C.muted }}>No records found.</td></tr>
              )}
              {filtered.map(function(row, i) {
                return (
                  <tr key={i} style={{ borderBottom: "1px solid #f3f4f6", background: i % 2 === 0 ? C.white : "#fafafa" }}>
                    <td style={{ padding: "8px 12px", fontWeight: 500 }}>{row.system}</td>
                    <td style={{ padding: "8px 12px", color: C.muted }}>{row.owner || "—"}</td>
                    <td style={{ padding: "8px 12px" }}>{row.futureOwner || "—"}</td>
                    <td style={{ padding: "8px 12px" }}><TrustBadge v={row.trust} /></td>
                    <td style={{ padding: "8px 12px", color: C.muted }}>{row.stories || "—"}</td>
                    <td style={{ padding: "8px 12px" }}><ActionBadge v={row.action} /></td>
                    <td style={{ padding: "8px 12px", fontWeight: 700, color: row.priority === "P0" ? C.red : row.priority === "P1" ? C.orange : C.text }}>{row.priority}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ marginTop: 10, fontSize: 11, color: C.muted }}>
        Live data from SharePoint · Data Source Master Tracker · {sources.length} systems tracked
      </div>
    </div>
  );
}
